import React, { useEffect, useState } from 'react';
import { FaTimes, FaRegCopy, FaCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import styles from './InfoCardModal.module.css';

/**
 * Çalışan Kartı ile aynı görsel dile sahip, yeniden kullanılabilir bilgi kartı iskeleti.
 * Müşteri ve Proje kartları bu bileşenin üzerine kuruludur.
 */

const EMPTY_VALUES = ['nan', 'nat', 'n/a', 'null', 'undefined'];

export const display = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const str = String(value).trim();
  if (!str || EMPTY_VALUES.includes(str.toLowerCase())) return '—';
  return str;
};

export const isEmptyValue = (value) => display(value) === '—';

export const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value.seconds) return new Date(value.seconds * 1000);
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatDate = (value) => {
  const d = toDate(value);
  return d
    ? d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : display(value);
};

export const yesNo = (value) => {
  if (value === true) return 'Evet';
  if (value === false) return 'Hayır';
  if (value === 'Yes') return 'Evet';
  if (value === 'No') return 'Hayır';
  return display(value);
};

// Tarihe kalan/geçen gün sayısını okunabilir ipucuna çevirir
export const daysHint = (value) => {
  const d = toDate(value);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diff = Math.round((target - today) / 86400000);
  if (diff === 0) return 'bugün';
  return diff > 0 ? `${diff} gün kaldı` : `${Math.abs(diff)} gün geçti`;
};

export const initialsFrom = (text, fallback = '?') => {
  const parts = String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return fallback;
  const letters = parts.slice(0, 2).map((p) => p.charAt(0));
  return letters.join('').toUpperCase();
};

const copyToClipboard = async (value, label) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} kopyalandı`);
  } catch {
    toast.error('Kopyalanamadı');
  }
};

// Tek bilgi satırı; copyable verildiğinde hover'da kopyalama düğmesi gösterir
export const InfoItem = ({ label, value, hint, copyable, href, wide }) => {
  const empty = isEmptyValue(value);
  return (
    <div className={`${styles.infoItem} ${wide ? styles.infoItemWide : ''}`}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={`${styles.infoValue} ${empty ? styles.infoValueEmpty : ''}`}>
        {href && !empty ? (
          <a href={href} className={styles.infoLink}>{value}</a>
        ) : (
          value
        )}
        {hint && !empty && <span className={styles.infoHint}>{hint}</span>}
        {copyable && !empty && (
          <button
            type="button"
            className={styles.copyButton}
            onClick={() => copyToClipboard(value, label)}
            title={`${label} kopyala`}
            aria-label={`${label} kopyala`}
          >
            <FaRegCopy />
          </button>
        )}
      </span>
    </div>
  );
};

export const InfoSection = ({ title, icon: Icon, children }) => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>
      {Icon && <Icon className={styles.sectionIcon} />}
      {title}
    </h3>
    {children}
  </section>
);

export const InfoGrid = ({ children, columns }) => (
  <div className={`${styles.infoGrid} ${columns === 2 ? styles.infoGridTwo : ''}`}>
    {children}
  </div>
);

export const StatGrid = ({ stats, loading }) => (
  <div className={styles.statGrid}>
    {stats.map((stat) => (
      <div key={stat.label} className={styles.statCard}>
        <span className={`${styles.statValue} ${loading ? styles.statLoading : ''}`}>
          {loading ? '…' : stat.value}
        </span>
        <span className={styles.statLabel}>{stat.label}</span>
      </div>
    ))}
  </div>
);

export const TagList = ({ items, emptyText = 'Kayıt bulunmuyor.' }) => {
  if (!items || items.length === 0) {
    return <p className={styles.emptyText}>{emptyText}</p>;
  }
  return (
    <div className={styles.tagList}>
      {items.map((item) => (
        <span key={item} className={styles.tag}>{item}</span>
      ))}
    </div>
  );
};

const STATUS_TONES = {
  active: styles.statusChipActive,
  inactive: styles.statusChipInactive,
  neutral: styles.statusChipNeutral,
  warning: styles.statusChipWarning,
};

const InfoCardModal = ({
  ariaLabel,
  title,
  subtitle,
  imageUrl,
  initials,
  status,          // { label, tone: 'active' | 'inactive' | 'neutral' | 'warning' }
  chips = [],      // hero'daki ek etiketler
  onClose,
  onEdit,
  editLabel = 'Düzenle',
  editIcon,
  children,
}) => {
  const { isDarkMode } = useTheme();
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const photoUrl = !imageFailed && imageUrl ? imageUrl : null;
  const visibleChips = chips.filter((chip) => !isEmptyValue(chip));

  return (
    <div
      className={`${styles.overlay} ${isDarkMode ? styles.darkMode : ''}`}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label={ariaLabel}>
        <div className={styles.hero}>
          <button className={styles.closeButton} onClick={onClose} aria-label="Kapat">
            <FaTimes />
          </button>
          <div className={styles.heroContent}>
            <span className={styles.avatar}>
              {photoUrl ? (
                <img
                  src={photoUrl}
                  alt={title || ariaLabel}
                  className={styles.avatarImg}
                  onError={() => setImageFailed(true)}
                />
              ) : (
                initials
              )}
            </span>
            <div className={styles.heroInfo}>
              <h2 className={styles.heroName}>{display(title)}</h2>
              {subtitle && <p className={styles.heroRole}>{display(subtitle)}</p>}
              <div className={styles.heroChips}>
                {status && (
                  <span
                    className={`${styles.statusChip} ${
                      STATUS_TONES[status.tone] || styles.statusChipNeutral
                    }`}
                  >
                    <FaCircle /> {status.label}
                  </span>
                )}
                {visibleChips.map((chip) => (
                  <span key={chip} className={styles.heroChip}>{chip}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.body}>{children}</div>

        <div className={styles.footer}>
          <button type="button" className={styles.secondaryButton} onClick={onClose}>
            Kapat
          </button>
          {onEdit && (
            <button type="button" className={styles.primaryButton} onClick={onEdit}>
              {editIcon}
              {editLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoCardModal;
